import { Trans } from 'react-i18next';

import { caseRoute } from '@/features/pixie/route';
import type { PixieAnswer, PixieData, PixieSkill, PixieT } from '@/features/pixie/types';
import { findCase } from '@/shared/domain/selectors';
import type { CaseRecord } from '@/shared/domain/case';

/* Lookup and navigation. These run ahead of the explanation intents so
   "open CLC-104882" navigates rather than being answered as a question.

   There is deliberately no orders destination and no conflicts queue here: the
   cockpit raises no ProductOrder, and conflicted cases surface under Needs
   attention. Asking for either falls through to a filtered list instead of
   opening a screen that does not exist.

   **What Pixie says is translated; what Pixie listens for is a keyword list.**
   The patterns below are not prose — they are the vocabulary a question may be
   phrased in, and they carry both languages at once so a German operator can
   type German and an English one can keep typing English. Two rules hold them
   together:

     - The suggestion chips in `answers.tsx` are catalogue strings. Their
       `question` is fed straight back through this matcher, so a chip and the
       pattern meant to catch it have to be edited together. `tests/features/
       pixie/skills.test.tsx` asserts that every chip in every locale still
       reaches a real answer, which is the check that keeps them in step.
     - German alternatives sit *outside* the `\b(…)\b` groups. `\b` is defined
       against `[A-Za-z0-9_]`, so `\böffnen\b` never matches — the boundary
       before `ö` does not exist. Umlauted stems are matched as plain
       substrings instead, which is safe in the other direction because no
       English word contains them. */

const OPEN =
  /\b(open|show me|show|go to|take me to|navigate|jump to|display)\b|öffne|zeige|gehe zu|springe zu|wechsle zu|anzeigen/i;
const LIST =
  /\b(list|find|search|which|what|any|how many|show)\b|auflisten|liste|welche|wie viele|finde|suche|zeige/i;
const EXPLICIT_LIST =
  /\b(list|which|find|how many|all of|search)\b|auflisten|liste|welche|wie viele|finde|suche/i;
const CASE_ID = /\b(CLC|MCD|CEX|NOM|PRK)-\d+\b/i;
const CI_ID = /\bCI\d{7}\b/i;

/** Ordered most specific first — a generic "queue" must not shadow "MACD queue".
    The third element is the `pixie:places` key, not a word: the record key is
    the catalogue key, so there is no second table to keep in step. */
const places = [
  [/\b(overview|dashboard|home)\b|übersicht|startseite/i, '/', 'overview'],
  [/\b(mail )?intake|inbox|incoming mail\b|posteingang|eingangspost/i, '/intake', 'intake'],
  [
    /\bawaiting reply|waiting on the (customer|requester)\b|ausstehende antwort|wartet auf den kunden|anfragenden/i,
    '/queue/awaiting',
    'awaiting',
  ],
  [/\bteam queue\b|teamvorrat|team-vorrat/i, '/queue/team', 'team'],
  [
    /\bservice catalog|service candidates?|service specifications?\b|servicekatalog|servicekandidat|servicespezifikation/i,
    '/catalog/service',
    'serviceCatalog',
  ],
  [/\bcustomers?|tenants?|sites?\b|kunden|mandanten|standorte/i, '/catalog/customers', 'customers'],
  [
    /\bcharacteristics?|mri config|merkmal|mri-konfiguration/i,
    '/admin/mri',
    'mriCharacteristics',
  ],
  [/\bclassification rules?\b|klassifizierungsregel/i, '/admin/classification', 'classification'],
  [
    /\bbusiness rules?|validation rules?\b|geschäftsregel|validierungsregel/i,
    '/admin/rules',
    'businessRules',
  ],
  [/\btemplates?\b|vorlage/i, '/admin/templates', 'templates'],
  [/\bthresholds?\b|schwellenwert/i, '/admin/thresholds', 'thresholds'],
  [/\banalytics|charts?\b|analyse|diagramm/i, '/analytics', 'analytics'],
  [/\breports?\b|bericht/i, '/reports', 'reports'],
  [/\bsettings|preferences|profile\b|einstellungen|profil/i, '/settings', 'settings'],
  [/\b(my queue|work queue|queue)\b|arbeitsvorrat|vorrat/i, '/queue', 'myQueue'],
] as const;

function gapPhrase(open: readonly string[], t: PixieT): string {
  return open.length > 0
    ? t('pixie:skills.gapsOpen', { count: open.length })
    : t('pixie:skills.gapsNone');
}

/** 1 — a case identifier anywhere in the question wins. */
const byCaseId: PixieSkill = (question, data) => {
  const hit = CASE_ID.exec(question);
  if (!hit) return null;

  const id = hit[0].toUpperCase();
  const record = findCase(data.cases, id);
  if (!record) {
    return {
      text: (
        <Trans
          ns="pixie"
          i18nKey="skills.notFound"
          values={{ id }}
          components={{ b: <b /> }}
        />
      ),
    };
  }

  const detail = gapPhrase(data.gaps[record.id] ?? [], data.t);
  if (OPEN.test(question)) {
    return {
      text: (
        <Trans
          ns="pixie"
          i18nKey="skills.opening"
          values={{
            id: record.id,
            customer: data.vocabulary.caseCustomer(record),
            state: data.vocabulary.caseState(record),
            detail,
          }}
          components={{ b: <b /> }}
        />
      ),
      navigate: caseRoute(record),
    };
  }
  return {
    text: (
      <Trans
        ns="pixie"
        i18nKey="skills.caseSummary"
        values={{
          id: record.id,
          customer: data.vocabulary.caseCustomer(record),
          offering: record.offering || data.t('pixie:skills.unmatchedService'),
          state: data.vocabulary.caseState(record),
          confidence: data.format.number(record.confidence, 2),
          detail,
        }}
        components={{ b: <b /> }}
      />
    ),
    list: [record],
  };
};

/** 2 — a message identifier resolves to the case it created. */
const byMessageId: PixieSkill = (question, data) => {
  const hit = CI_ID.exec(question);
  if (!hit) return null;

  const id = hit[0].toUpperCase();
  const mail = data.mails.find((entry) => entry.id === id);
  if (!mail) return { text: data.t('pixie:skills.noMessage') };

  const record = findCase(data.cases, mail.case);
  const navigate = OPEN.test(question) && record ? caseRoute(record) : undefined;
  return {
    text: (
      <Trans
        ns="pixie"
        i18nKey="skills.messageSummary"
        values={{
          id: mail.id,
          from: mail.from,
          type: data.vocabulary.orderType(mail.type),
          queue: data.vocabulary.queue(mail.routed),
        }}
        components={{ b: <b /> }}
      />
    ),
    list: record ? [record] : [],
    cites: ['classification'],
    ...(navigate === undefined ? {} : { navigate }),
  };
};

/** 3 — plain screen navigation. Runs before listing so "show me the MACD queue"
    opens the queue, while "list MACD cases" still returns rows. */
const byPlace: PixieSkill = (question, data) => {
  if (!OPEN.test(question) || EXPLICIT_LIST.test(question)) return null;
  for (const [pattern, to, key] of places) {
    if (pattern.test(question)) {
      return {
        text: data.t('pixie:skills.openingPlace', { place: data.t(`pixie:places.${key}`) }),
        navigate: to,
      };
    }
  }
  return null;
};

/* 4 — filtered case lists. Each rule that fires narrows the rows and records the
   phrase it narrowed by, so the answer can say what it actually did. */

interface Filtered {
  readonly rows: readonly CaseRecord[];
  readonly applied: readonly string[];
}

/** The line below which a case is called low-confidence, and the same number the
    phrase quotes back — so the two cannot drift, and so the number is written
    the way the reader's language writes it. */
const LOW_CONFIDENCE = 0.7;

const queueRules = [
  [
    /\b(catalog|service) exceptions?\b|katalogausnahme|serviceausnahme|service-ausnahme/i,
    'catalog',
    'catalogExceptions',
  ],
  [/\bmacd\b/i, 'macd', 'macd'],
  [/\bparked|on hold\b|geparkt|angehalten/i, 'parked', 'parked'],
  [/\bnon-?order\b|auftragsfrei/i, 'non-order', 'nonOrder'],
  [/\bwork queue|in clarification\b|arbeitsvorrat|in klärung/i, 'work', 'work'],
] as const;

function filterCases(question: string, data: PixieData): Filtered {
  const { t } = data;
  let rows: readonly CaseRecord[] = data.cases;
  const applied: string[] = [];
  const asked = question.toLowerCase();

  for (const [pattern, queue, key] of queueRules) {
    if (pattern.test(question)) {
      rows = rows.filter((record) => record.queue === queue);
      applied.push(t(`pixie:filters.${key}`));
    }
  }

  if (/\bconflicts?\b|widerspruch|widersprüche|konflikt/i.test(question)) {
    rows = rows.filter((record) => record.disposition === 'conflict');
    applied.push(t('pixie:filters.conflict'));
  }
  if (/\bcomplete|ready|sign.?off\b|vollständig|fertig|freigegeben/i.test(question)) {
    rows = rows.filter((record) => record.disposition === 'ready');
    applied.push(t('pixie:filters.complete'));
  }
  if (
    /\bawaiting reply|waiting on the (customer|requester)\b|ausstehende antwort|wartet auf den kunden|anfragenden/i.test(
      question,
    )
  ) {
    /* On the desk key, not on the word the column shows. These two filters used
       to compare against `'Requester'` and `'Order Manager'` — English display
       labels — so in German they would have matched nothing and the assistant
       would have answered "no cases" to a question that had plenty. */
    rows = rows.filter((record) => record.deskKey === 'requester');
    applied.push(t('pixie:filters.requester'));
  }
  if (
    /\b(my turn|waiting on me|need(s)? (a )?decision|need my)\b|entscheidung|wartet auf mich|bin ich am zug/i.test(
      question,
    )
  ) {
    rows = rows.filter((record) => record.deskKey === 'orderManager');
    applied.push(t('pixie:filters.you'));
  }
  if (/\blow confidence\b|niedrige konfidenz|geringe konfidenz/i.test(question)) {
    rows = rows.filter((record) => record.confidence < LOW_CONFIDENCE);
    applied.push(
      t('pixie:filters.lowConfidence', { threshold: data.format.number(LOW_CONFIDENCE, 2) }),
    );
  }

  const field = data.characteristics.find(
    (characteristic) =>
      asked.includes(characteristic.businessName.toLowerCase()) ||
      asked.includes(characteristic.name.toLowerCase()),
  );
  if (field && /\bmissing|without|no |outstanding\b|fehlt|fehlend|ohne |offen/i.test(question)) {
    rows = rows.filter((record) => (data.gaps[record.id] ?? []).includes(field.name));
    /* The business name as the catalogue spells it. It used to be lower-cased on
       the way in, which reads as a phrase in English and as a mistake in German,
       where a common noun keeps its capital wherever it stands in the sentence. */
    applied.push(t('pixie:filters.missingField', { field: field.businessName }));
  }

  // Matched on the first word so "Aurora" finds "Aurora Health Group".
  const customer = data.customers.find((entry) =>
    asked.includes((entry.name.split(' ')[0] ?? '').toLowerCase()),
  );
  if (customer) {
    const first = customer.name.split(' ')[0] ?? customer.name;
    /* Against the name the record carries, not against `caseCustomer()`: this is
       a match on master data, and "Unresolved" is a word for the reader rather
       than a customer anybody could be asking about. A case with no name simply
       is not this customer's — the optional call answers that without a guard. */
    rows = rows.filter((record) => record.customer?.split(' ')[0] === first);
    applied.push(t('pixie:filters.forCustomer', { customer: customer.name }));
  }

  return { rows, applied };
}

const SHOWN = 8;

const byFilter: PixieSkill = (question, data) => {
  if (!LIST.test(question) && !/\border|case/i.test(question)) return null;
  if (
    !/\border|case|request|queue|exception|conflict|missing|waiting|decision|fälle|anfrage|vorrat|ausnahme|widerspruch|konflikt|fehlt|wartet|entscheidung/i.test(
      question,
    )
  ) {
    return null;
  }

  const { rows, applied } = filterCases(question, data);
  if (applied.length === 0) return null;

  const phrase = applied.join(` ${data.t('pixie:filters.and')} `);
  if (rows.length === 0) return { text: data.t('pixie:skills.noMatch', { phrase }) };

  return {
    text: (
      <Trans
        ns="pixie"
        i18nKey={rows.length > SHOWN ? 'skills.hitsShown' : 'skills.hits'}
        count={rows.length}
        values={{ count: rows.length, phrase, shown: SHOWN }}
        components={{ b: <b /> }}
      />
    ),
    list: rows.slice(0, SHOWN),
  };
};

const skills: readonly PixieSkill[] = [byCaseId, byMessageId, byPlace, byFilter];

/** The first skill that claims the question answers it. */
export function runSkills(question: string, data: PixieData): PixieAnswer | null {
  for (const skill of skills) {
    const answer = skill(question, data);
    if (answer) return answer;
  }
  return null;
}
